import axios from "axios";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

// Create a configured Axios instance
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    "Content-Type": "application/json",
  },
});

// --- Request Interceptor ---
// Automatically adds the JWT token (if it exists) to the Authorization header
// of every outgoing request.
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem("authToken");
    if (token) {
      config.headers["Authorization"] = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  },
);

// --- RESPONSE INTERCEPTOR (Handles Token Expiration) ---
api.interceptors.response.use(
  (response) => {
    // Any status code within the range of 2xx causes this function to trigger
    return response;
  },
  (error) => {
    // Any status codes that falls outside the range of 2xx causes this function to trigger

    // Check specific error structure from your backend
    const isTokenExpired =
      error.response?.data?.error?.code === "TOKEN_EXPIRED" ||
      error.response?.data?.message === "Token has expired";

    // Check standard 401 Unauthorized status
    const isUnauthorized = error.response?.status === 401;

    if (isTokenExpired || isUnauthorized) {
      console.warn("Session expired. Redirecting to login...");

      // 1. Clear stored data
      localStorage.removeItem("authToken");
      localStorage.removeItem("user");

      // 2. Force redirect to login page
      // We use window.location because we are outside of a React Component
      if (window.location.pathname !== "/login") {
        window.location.href = "/login?sessionExpired=true";
      }
    }

    return Promise.reject(error);
  },
);

// --- API CALLS DEFINITIONS ---

// 1. Authentication
export const login = (email, password) =>
  api.post("/v1/auth/login", { email, password });
export const validateResetToken = (token) =>
  api.post(`/v1/scope-token/verify`, { token });
export const setNewPassword = (token, password, confirmPassword, tokenType) =>
  api.post("/v1/auth/set-password", {
    token,
    password,
    confirmPassword,
    tokenType,
  });
export const forgotPassword = (email) =>
  api.post("/v1/scope-token/forgot-password", null, {
    params: { email },
  });

// 2. User Management
export const fetchUserList = (payload) =>
  api.post("/v1/smart-users/list", payload);
export const createUser = (userData) => api.post("/v1/smart-users", userData);
// Assuming your API supports getting a single user by ID
export const getSmartUser = (id) => api.get(`/v1/smart-users/${id}`);
export const updateUserProfile = (data) =>
  api.put(`/v1/smart-users/me/profile`, data);
export const updateUser = (id, data) =>
  api.put(`/v1/smart-users/${id}/profile`, data);
export const deleteUser = (id) => api.delete(`/v1/smart-users/${id}`);
export const fetchUserDashboardCounts = (payload) =>
  api.get("/v1/counts/smart-users", payload);
export const fetchEntityList = () => api.get("/v1/entities/dropdown/list");
export const deactivateUser = (id, reason) =>
  api.patch(`/v1/smart-users/${id}/deactivate`, {
    reason: reason,
    confirm: true,
  });
export const reactivateUser = (id) =>
  api.patch(`/v1/smart-users/${id}/reactivate`);

// 3. Customer Management
export const fetchCustomerList = (payload) =>
  api.post("/v1/customers/list", payload);
export const getDeviceDeeplink = (deviceId) =>
  api.post(`/v1/devices/${deviceId}/get-deeplink`);
export const fetchApplicableProducts = (entityId) =>
  api.get(`/v1/orders/get-applicable-products/${entityId}`);
export const createCustomer = (customerData) =>
  api.post("/v1/customers", customerData);
export const getCustomer = (id) => api.get(`/v1/customers/${id}`);
export const updateCustomer = (id, data) =>
  api.put(`/v1/customers/${id}/profile`, data);
export const deleteCustomer = (id) => api.delete(`/v1/customers/${id}`);
export const deactivateCustomer = (id, reason) =>
  api.post(`/v1/customers/${id}/deactivate`, { deactivationReason: reason });
export const fetchCustomerDashboardCounts = (payload) =>
  api.get("/v1/counts/customers", payload);

// 3b. Case Manager / CRE - Customer Assignment
export const fetchCaseManagerAssignableCustomers = (userId, payload) =>
  api.post(`/v1/case-managers/${userId}/customers/assignable`, payload);

export const fetchCaseManagerAssignedCustomers = (userId, payload) =>
  api.post(`/v1/case-managers/${userId}/customers/list`, payload);

// Replace a case manager's assigned customers
export const replaceCaseManagerAssignedCustomers = (userId, payload) =>
  api.put(`/v1/case-managers/${userId}/customers`, payload);

// Fetch case managers dropdown for a specific client
export const fetchCaseManagerDropdown = (clientId) =>
  api.get(`/v1/case-managers/clients/${clientId}/dropdown`);

// Additively assign customers to a case manager (POST – does not remove existing)
export const addCaseManagerCustomers = (userId, payload) =>
  api.post(`/v1/case-managers/${userId}/customers`, payload);

// --- NEW: Check Email Existence ---
export const checkCustomerEmail = (email) =>
  api.post("/v1/customers/validate-email", null, {
    params: { email },
  });

// --- NEW: Check Record ID Existence ---
export const checkRecordId = (recordId) =>
  api.get("/v1/customers/validate-record-id", {
    params: { record_id: recordId }, // <-- Updated param name to record_id
  });

// --- NEW: Transactions & Invoices ---
export const fetchCustomerTransactions = (customerId, params) =>
  api.post(`/v1/receipts/customer/${customerId}/list`, { params });
// Using blob response type for file download
export const downloadInvoice = (invoiceId) =>
  api.get(`/v1/receipts/${invoiceId}/download`, { responseType: "blob" });

// 4. Device Management
export const fetchDevices = (payload) => api.post("/v1/devices/list", payload);
export const createDevice = (deviceData) => api.post("/v1/devices", deviceData);
export const getDevice = (id) => api.get(`/v1/devices/${id}`);
export const fetchDeviceTelemetryLatest = (deviceId) =>
  api.get(`/v1/devices/${deviceId}/telemetry/latest`);
export const updateDevice = (id, data) => api.put(`/v1/devices/${id}`, data);
export const deleteDevice = (id) => api.delete(`/v1/devices/${id}`);
export const fetchDeviceDashboardCounts = (payload) =>
  api.get("/v1/counts/devices", payload);

// 5. My Profile
// These assume endpoints exist relative to the authenticated user or generic payment methods
export const fetchPaymentMethods = () => api.get("/v1/payment-methods/list");
export const fetchPaymentMethodsByEntity = (entityId) =>
  api.get(`/v1/payment-methods/entity/${entityId}/list`); // Add Customer
export const createPaymentMethod = (data) =>
  api.post("/v1/payment-methods/add", data);
export const updatePaymentMethod = (id, data) =>
  api.put(`/v1/payment-methods/${id}`, data);
export const deletePaymentMethod = (id) =>
  api.delete(`/v1/payment-methods/${id}`);

// 6. Entity Management
export const fetchEntityCounts = () => api.get("/v1/counts/entities");
export const createEntity = (payload) => api.post("/v1/entities", payload);
export const getEntity = (id) => api.get(`/v1/entities/${id}`);
export const updateEntity = (id, payload) =>
  api.put(`/v1/entities/${id}`, payload);
export const deactivateEntityClient = (id, reason) =>
  api.patch(`/v1/entities/${id}/deactivate`, { reason: reason, confirm: true });
export const fetchEntityDropDownList = () => api.get("/v1/entities/dropdown");
// 7. Client Management
export const fetchClientCounts = () => api.get("/v1/counts/clients");
export const fetchClientDropDownList = (entityId) =>
  api.get(`/v1/entities/${entityId}/clients/dropdown`);

//8.Roles and Permsisions
export const fetchRolesAndPermissions = (customizationType) =>
  api.get("/v1/rbac/roles/dropdown", {
    params: customizationType
      ? { assignable_scope: customizationType }
      : undefined,
  });
export const fetchPermissions = (id, roleId) =>
  api.get(`/v1/rbac/entities/${id}/roles/${roleId}/permissions`);
export const fetchPlatformPermissions = (roleId) =>
  api.get(`/v1/rbac/roles/${roleId}/permissions`);
export const saveRolePermissions = (id, roleId, payload) =>
  api.put(`/v1/rbac/entities/${id}/roles/${roleId}/permissions`, payload);
export const savePlatformRolePermissions = (roleId, payload) =>
  api.put(`/v1/rbac/roles/${roleId}/permissions`, payload);

// 9. Reports
export const fetchEnrollmentStatusReport = (code, payload) =>
  api.post(`/v1/reports/${code}/run`, payload);
export const exportEnrollmentStatusReport = (code, payload) =>
  api.post(`/v1/reports/${code}/export`, payload, { responseType: "blob" });

// --- NEW: Unassigned Inventory Search ---
// These endpoints expect query params like ?search=xyz&page=1&limit=10
export const fetchUnassignedDevices = (params, id) =>
  api.get(`/v1/devices/unassigned/imei/${id}`, { params });
export const fetchUnassignedSims = (params, id) =>
  api.get(`/v1/sims/unassigned/iccid/${id}`, { params });

// --- NEW: Sync SIM Telco ---
export const syncSimTelco = (simId) =>
  api.patch(`/v1/sims/sync-telco/${simId}`);

// Export the axios instance as default in case you need to make custom calls
export default api;
